import { useEffect } from "react";
import { site } from "@/config/site";
import { canonical, personJsonLd } from "@/lib/seo";

type Props = {
  title: string;
  description: string;
  path: string;
  image?: string;
  jsonLd?: object | object[];
};

function upsertMeta(selector: string, attributes: Record<string, string>) {
  let element = document.head.querySelector(selector) as HTMLMetaElement | null;
  if (!element) {
    element = document.createElement("meta");
    document.head.appendChild(element);
  }
  for (const [key, value] of Object.entries(attributes)) {
    element.setAttribute(key, value);
  }
}

export function Seo({ title, description, path, image, jsonLd }: Props) {
  useEffect(() => {
    const fullTitle = title.includes(site.name) ? title : `${title} — ${site.name}`;
    const url = canonical(path);
    const ogImage = image
      ? image.startsWith("http")
        ? image
        : `${site.origin}${image}`
      : `${site.origin}${site.portraits.primary.src}`;
    document.title = fullTitle;

    upsertMeta('meta[name="description"]', {
      name: "description",
      content: description,
    });
    upsertMeta('meta[property="og:title"]', {
      property: "og:title",
      content: fullTitle,
    });
    upsertMeta('meta[property="og:description"]', {
      property: "og:description",
      content: description,
    });
    upsertMeta('meta[property="og:url"]', { property: "og:url", content: url });
    upsertMeta('meta[property="og:image"]', {
      property: "og:image",
      content: ogImage,
    });
    upsertMeta('meta[property="og:type"]', {
      property: "og:type",
      content: "website",
    });
    upsertMeta('meta[name="twitter:card"]', {
      name: "twitter:card",
      content: "summary_large_image",
    });

    let link = document.head.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!link) {
      link = document.createElement("link");
      link.rel = "canonical";
      document.head.appendChild(link);
    }
    link.href = url;

    const graph = Array.isArray(jsonLd)
      ? jsonLd
      : jsonLd
        ? [jsonLd]
        : [personJsonLd()];
    let script = document.head.querySelector(
      "script[data-seo-jsonld]",
    ) as HTMLScriptElement | null;
    if (!script) {
      script = document.createElement("script");
      script.type = "application/ld+json";
      script.dataset.seoJsonld = "true";
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(graph);
  }, [title, description, path, image, jsonLd]);

  return null;
}
